import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
	SearchBar,
	Alert,
	AlertTitle,
	Spinner,
	PlatformMark,
	useToast,
	EmptyState
} from '../ui';
import { AddAccountForm, AccountCard } from '../composite';
import { ClientActionsDropdown } from '../composite/ClientActionsDropdown';
import { DeletionStatusPanel } from '../features/clients/DeletionStatusPanel';
import { POSITION_ORDER, positionMeta } from '../../constants/standing';
import { useClientDetail } from '../features/clients';
import { PersonIcon } from '@radix-ui/react-icons';
import { PATHS } from '../../constants/Paths';

/**
 * ClientDetail - Page component for viewing client details and managing accounts
 * 
 * Features:
 * - Display client information
 * - List all accounts for the client
 * - Add new accounts
 * - Refresh account data
 * - Search and filter accounts
 */
export const ClientDetailPage = () => {
	const toast = useToast();
	const { id: clientId } = useParams();
	const navigate = useNavigate();
	const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);

	// Use business logic hook
	const {
		client,
		accounts,
		filteredAccounts,
		searchTerm,
		selectedPlatform,
		platformOptions,
		loading,
		error,
		refreshAccounts,
		updateSearch,
		updatePlatformFilter,
		resetFilters,
		formatDate,
		formatNumber,
		getRiskColor,
		isEmpty
	} = useClientDetail(clientId);

	// Coverage gap, same 14-day rule the account cards use: an account nobody
	// has scanned in a fortnight is silently unmonitored, not safe.
	const staleAccounts = accounts.filter((a) => {
		if (!a.lastScan) return true;
		return (Date.now() - new Date(a.lastScan).getTime()) / 86400000 > 14;
	}).length;

	// Handle account creation
	const handleAccountCreated = (accountData) => {
		toast.success(`@${accountData?.handle || 'account'} is now monitored`);
		refreshAccounts();
		setIsAddAccountModalOpen(false);
	};

	if (loading && !client) {
		return (
			<div className="min-h-screen bg-muted flex items-center justify-center">
				<Spinner size="lg" />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted">
			<div className="max-w-7xl mx-auto p-4 sm:p-6">
				{/* Header */}
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
					<div className="min-w-0">
						<button
							onClick={() => navigate('/clients')}
							className="text-sm text-muted-foreground hover:text-foreground mb-2 flex items-center gap-1"
						>
							← Back to Clients
						</button>
						<h1 className="text-2xl sm:text-3xl font-semibold text-foreground truncate">
							{client?.name || 'Client'}
						</h1>
						<p className="text-sm sm:text-base text-muted-foreground mt-1 capitalize">
							{client?.category || 'Individual'} · {accounts.length} account{accounts.length === 1 ? '' : 's'} monitored
						</p>
					</div>

					<div className="flex items-center gap-3 shrink-0">
						<Button variant="outline" onClick={() => setIsAddAccountModalOpen(true)}>Add account</Button>
						<Button onClick={() => navigate(PATHS.NEW_SCANS, { state: { clientId } })}>New scan</Button>
						{client && <ClientActionsDropdown client={client} showView={false} onAddAccount={() => setIsAddAccountModalOpen(true)} onDeletionAccepted={() => navigate('/clients')} />}
					</div>
				</div>
				<DeletionStatusPanel />

				{/* Client summary */}
				{client && (
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
						{[
							{ label: 'Accounts', value: accounts.length },
							{ label: 'Exposure', value: `${client.risk ?? client.overallRisk ?? 0}%` },
							{ label: 'Needs a scan', value: staleAccounts, tone: staleAccounts > 0 ? 'text-amber-600' : undefined },
							{ label: 'Last scan', value: formatDate(client.lastScan) },
						].map((t) => (
							<div key={t.label} className="rounded-xl border border-border bg-card shadow-card p-4">
								<div className="text-xs text-muted-foreground">{t.label}</div>
								<div className={`mt-1 text-lg font-semibold capitalize truncate ${t.tone || 'text-foreground'}`}>{t.value}</div>
							</div>
						))}
					</div>
				)}

				{/* Error State */}
				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertTitle>Error loading client data</AlertTitle>
						<p>{error}</p>
					</Alert>
				)}

				{/* Search and Filters */}
				<div className="mb-4">
					<div>
						<div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
							<div className="flex-1 min-w-0">
								<SearchBar
									searchTerm={searchTerm}
									onSearchChange={updateSearch}
									searchPlaceholder="Search accounts by handle, name, or bio..."
									showFilters={false}
								/>
							</div>
						<div className="flex flex-wrap items-center gap-2">
							{platformOptions.map(option => {
								const active = selectedPlatform === option.value;
								const count = option.value
									? accounts.filter(a => a.platform === option.value).length
									: accounts.length;
								return (
									<button
										key={option.value || 'all'}
										type="button"
										onClick={() => updatePlatformFilter(option.value)}
										className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
											active
												? 'border-brand-600 bg-brand-50 text-brand-700 font-medium dark:bg-brand-500/10 dark:text-brand-200'
												: 'border-border bg-card text-muted-foreground hover:bg-muted'
										}`}
									>
										{option.value && <PlatformMark platform={option.value} size="xs" />}
										{option.value || 'All'}
										<span className={active ? 'text-brand-500' : 'text-muted-foreground'}>{count}</span>
									</button>
								);
							})}
						</div>
						</div>
					</div>
				</div>

				{/* Where the roster stands, worst first - a triage line, not a stat. */}
				{accounts.some((a) => a.standing?.position) && (
					<div className="flex flex-wrap items-center gap-2 text-sm">
						<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Standing</span>
						{POSITION_ORDER.map((key) => {
							const n = accounts.filter((a) => a.standing?.position === key).length;
							if (!n) return null;
							const m = positionMeta(key);
							return (
								<span key={key} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${m.chip}`} title={m.meaning}>
									<span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
									{n} {m.label}
								</span>
							);
						})}
					</div>
				)}

				{/* Accounts List */}
				<div className="grid gap-4">
					{filteredAccounts.length === 0 ? (
						<Card>
							<CardContent>
								<EmptyState
									icon={PersonIcon}
									title={isEmpty ? 'No accounts yet' : 'No accounts match your filters'}
									description={isEmpty
										? 'Add the accounts you want monitored for this client.'
										: 'Try a different search or platform filter.'}
									action={isEmpty ? (
										<Button onClick={() => setIsAddAccountModalOpen(true)}>Add first account</Button>
									) : (
										<Button variant="outline" onClick={resetFilters}>Clear filters</Button>
									)}
								/>
							</CardContent>
						</Card>
					) : (
						filteredAccounts.map((account) => (
							<AccountCard
								key={account._id}
								account={account}
								clientId={clientId}
								formatDate={formatDate}
								formatNumber={formatNumber}
								getRiskColor={getRiskColor}
								onRefreshComplete={refreshAccounts}
							/>
						))
					)}
				</div>

				{/* Add Account Modal */}
				<AddAccountForm
					isOpen={isAddAccountModalOpen}
					onOpenChange={setIsAddAccountModalOpen}
					clientId={clientId}
					clientName={client?.name}
					onSuccess={handleAccountCreated}
				/>
			</div>
		</div>
	);
};
